import { z } from 'zod';

/** What coaches can be told about. */
export const NOTIFICATION_KINDS = ['unavailable', 'away', 'coming', 'joined', 'left', 'join_request', 'fixture'] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NOTIFICATION_LABELS: Record<NotificationKind, { title: string; hint: string }> = {
  unavailable: { title: 'Can’t make it', hint: 'A player or parent says they can’t make a session or game.' },
  away: { title: 'Away for a while', hint: 'Someone marks a player away for a stretch, like a holiday or camp.' },
  coming: { title: 'Coming after all', hint: 'Someone who said they couldn’t make it now can.' },
  joined: { title: 'Someone joins', hint: 'An invite is accepted, or a join request is approved.' },
  left: { title: 'Someone leaves', hint: 'A member leaves the team.' },
  join_request: { title: 'Join requests', hint: 'Someone asks to join from the team’s join link. Emails come once a day at most.' },
  fixture: { title: 'PlayHQ changes', hint: 'A game’s time or venue changes in PlayHQ, or the sync stops working.' },
};

export interface NotificationChoice {
  app: boolean;
  email: boolean;
}

/** Starting choices: everything in the app; emails for absences and join requests. */
export function defaultNotification(kind: NotificationKind): NotificationChoice {
  return { app: true, email: kind === 'unavailable' || kind === 'away' || kind === 'join_request' };
}

export type EmailMode = 'instant' | 'daily';

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  link: string | null;
  teamName: string | null;
  read: boolean;
  createdAt: string;
}

export interface NotificationList {
  unread: number;
  items: NotificationItem[];
}

export interface NotificationSettings {
  kinds: Record<NotificationKind, NotificationChoice>;
  emailMode: EmailMode;
  mutedTeamIds: string[];
  /** Teams you coach (the ones notifications come from). */
  teams: { id: string; name: string }[];
}

const choice = z.object({ app: z.boolean(), email: z.boolean() });
export const notificationSettingsInput = z.object({
  kinds: z.object(Object.fromEntries(NOTIFICATION_KINDS.map((k) => [k, choice])) as Record<NotificationKind, typeof choice>),
  emailMode: z.enum(['instant', 'daily']),
  mutedTeamIds: z.array(z.string().uuid()).max(100),
});

export const markReadInput = z.object({ ids: z.array(z.string().uuid()).max(200).optional() });
